/**
 * Gas Price Model - Encapsulates gas price grade data and provides clean interface for UI components
 * This model handles data transformation, validation, and formatted properties
 */
class GasPriceModel {
  private _serverData: any;

  constructor(serverData: any = null) {
    this._serverData = serverData;
  }

  // ===== SERVER DATA PROPERTIES =====

  get id() {
    return this._serverData?.id ?? '';
  }

  get store_id() {
    return this._serverData?.store_id ?? '';
  }

  get grade_id() {
    return this._serverData?.grade_id ?? '';
  }

  get grade_name() {
    return this._serverData?.grade_name ?? '';
  }

  get weighted_cost() {
    return this._serverData?.weighted_cost ?? null;
  }

  get current_price() {
    return this._serverData?.current_price ?? null;
  }

  get new_price() {
    return this._serverData?.new_price ?? '';
  }

  get last_price_update() {
    return this._serverData?.last_price_update ?? '';
  }

  get status() {
    return this._serverData?.status ?? 'draft';
  }

  // ===== FORMATTED PROPERTIES =====

  get formattedGradeName() {
    return this.grade_name || '-';
  }

  get formattedWeightedCost() {
    return this._formatPrice(this.weighted_cost);
  }

  get formattedCurrentPrice() {
    return this._formatPrice(this.current_price);
  }

  get formattedLastPriceUpdate() {
    return this._formatDateTime(this.last_price_update);
  }

  // ===== HELPER METHODS =====

  _formatPrice(value: string | null | undefined): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return `$${Number(value).toFixed(3)}`;
  }

  // Static method to format price to 3 decimal places (can be used without model instance)
  static formatPriceToThreeDecimals(value: string | number | null | undefined): string {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';
    return numValue.toFixed(3);
  }

  _formatDateTime(dateString: string): string {
    if (!dateString) {
      return '-';
    }
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    return `${month}/${day}/${year} ${displayHours}:${minutes}:${seconds} ${ampm}`;
  }

  // ===== UTILITY METHODS =====

  // Get raw server data (for backward compatibility or when raw API data is needed)
  getRawData() {
    return this._serverData;
  }

  // ===== STATIC METHODS =====

  static fromServerData(serverData: any) {
    return new GasPriceModel(serverData);
  }

  static createEmpty() {
    return new GasPriceModel();
  }
}

export default GasPriceModel;


